import { EmailTemplateType } from '../../store/emailTemplateApi';

export interface VariableInfo {
  name: string;
  label: string;
  description: string;
  sampleValue: string;
}

export interface TemplateSectionConfig {
  label: string;
  description: string;
  defaultSubject: string;
  defaultGreeting: string;
  defaultBodyParagraphs: string[];
  defaultCtaLabel: string;
  defaultClosingText: string;
  /** Variables allowed for this template type */
  variables: VariableInfo[];
  maxParagraphs: number;
  /** Which variable is used for the CTA href (locked) */
  ctaLinkVariable: string;
}

/** The 5 customizable template types */
export const EDITABLE_TEMPLATE_TYPES = [
  EmailTemplateType.PROMOTER_MAGIC_LINK,
  EmailTemplateType.PROSPECT_MAGIC_LINK,
  EmailTemplateType.PASSWORDLESS_LOGIN,
  EmailTemplateType.TEAM_MEMBER_INVITE,
  EmailTemplateType.REWARD_GIFT_CARD,
  EmailTemplateType.REWARD_DISCOUNT,
  EmailTemplateType.REWARD_CHOICE,
  EmailTemplateType.SURVEY_INVITATION,
] as const;

export type EditableTemplateType = (typeof EDITABLE_TEMPLATE_TYPES)[number];

export const TEMPLATE_TYPE_LABELS: Record<EditableTemplateType, string> = {
  [EmailTemplateType.PROMOTER_MAGIC_LINK]: 'Invite Promoter',
  [EmailTemplateType.PROSPECT_MAGIC_LINK]: 'Invite Prospect',
  [EmailTemplateType.PASSWORDLESS_LOGIN]: 'Passwordless Login',
  [EmailTemplateType.TEAM_MEMBER_INVITE]: 'Invite Team Member',
  [EmailTemplateType.REWARD_GIFT_CARD]: 'Reward — Gift Card',
  [EmailTemplateType.REWARD_DISCOUNT]: 'Reward — Discount',
  [EmailTemplateType.REWARD_CHOICE]: 'Reward — Choice',
  [EmailTemplateType.SURVEY_INVITATION]: 'Survey Invitation',
};

const PROMOTER_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Promoter's first name", sampleValue: 'John' },
  { name: 'companyName', label: 'Company Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your full name", sampleValue: 'Jane Smith' },
  { name: 'invitationReason', label: 'Invitation Reason', description: 'Why the promoter is being invited', sampleValue: 'Share your success story' },
  { name: 'productNames', label: 'Product Names', description: 'Your product names (auto-filled from settings)', sampleValue: 'Product A, Product B' },
];

const PROSPECT_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Prospect's first name", sampleValue: 'John' },
  { name: 'companyName', label: 'Company Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your full name", sampleValue: 'Jane Smith' },
  { name: 'invitationReason', label: 'Invitation Reason', description: 'Why the prospect is being invited', sampleValue: 'Discover our solution' },
  { name: 'promoterName', label: 'Promoters', description: 'Assigned promoter names (comma-separated if more than one)', sampleValue: 'Jane Smith, Mark Lee' },
  { name: 'promoterCompany', label: 'Promoter Companies', description: "Assigned promoters' companies (comma-separated)", sampleValue: 'Globex, Initech' },
];

const PASSWORDLESS_LOGIN_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "User's first name", sampleValue: 'John' },
  { name: 'companyName', label: 'Company Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your full name", sampleValue: 'Jane Smith' },
  { name: 'invitationReason', label: 'Invitation Reason', description: 'Reason for signing in', sampleValue: 'Discover our solution' },
];

const TEAM_MEMBER_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Team member's first name", sampleValue: 'John' },
  { name: 'inviterName', label: 'Inviter Name', description: 'Name of the person who invited them', sampleValue: 'Jane Smith' },
  { name: 'companyName', label: 'Company Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'role', label: 'Role', description: "Team member's assigned role", sampleValue: 'Account Manager' },
];

const SURVEY_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Recipient's first name", sampleValue: 'John' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your full name", sampleValue: 'Jane Smith' },
  { name: 'companyName', label: 'Company Name', description: "Your company name", sampleValue: 'Acme Inc' },
];

const REWARD_GIFT_CARD_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Promoter's first name", sampleValue: 'John' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'currency', label: 'Currency', description: 'Reward currency', sampleValue: 'USD' },
  { name: 'rewardAmount', label: 'Reward Amount', description: 'The reward value', sampleValue: '50.00' },
  { name: 'activityType', label: 'Activity Type', description: 'What earned the reward', sampleValue: 'Completed referral' },
];

const REWARD_DISCOUNT_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Promoter's first name", sampleValue: 'John' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'activityType', label: 'Activity Type', description: 'What earned the reward', sampleValue: 'Completed referral' },
  { name: 'discountName', label: 'Discount Name', description: 'Name/label of the discount', sampleValue: '20% off your next renewal' },
  { name: 'discountValue', label: 'Discount Value', description: 'The discount value', sampleValue: '20' },
  { name: 'discountType', label: 'Discount Type', description: 'percentage, credit_points or free_months', sampleValue: 'percentage' },
  { name: 'discountProductName', label: 'Product', description: 'Product the discount applies to', sampleValue: 'Pro Plan' },
  { name: 'discountTerms', label: 'Terms', description: 'Terms and conditions', sampleValue: 'Valid on your next invoice.' },
  { name: 'discountValidityDays', label: 'Validity (days)', description: 'How long the discount stays valid', sampleValue: '30' },
];

const REWARD_CHOICE_VARIABLES: VariableInfo[] = [
  { name: 'firstName', label: 'First Name', description: "Promoter's first name", sampleValue: 'John' },
  { name: 'vendorName', label: 'Vendor Name', description: "Your company name", sampleValue: 'Acme Inc' },
  { name: 'currency', label: 'Currency', description: 'Reward currency', sampleValue: 'USD' },
  { name: 'rewardAmount', label: 'Reward Amount', description: 'The gift card value', sampleValue: '50.00' },
  { name: 'discountName', label: 'Discount Name', description: 'The alternative discount', sampleValue: '20% off your next renewal' },
  { name: 'activityType', label: 'Activity Type', description: 'What earned the reward', sampleValue: 'Completed referral' },
];

export const TEMPLATE_CONFIGS: Record<EditableTemplateType, TemplateSectionConfig> = {
  [EmailTemplateType.PROMOTER_MAGIC_LINK]: {
    label: 'Invite Promoter',
    description: 'Sent when you invite a new promoter to join your program',
    defaultSubject: "You've been invited to join {{companyName}} on Clareefai",
    defaultGreeting: 'Hi {{firstName}},',
    defaultBodyParagraphs: [
      '{{vendorName}} from {{companyName}} invited you to join their Clareefai workspace and share your experience with {{productNames}}.',
      'Your feedback helps {{companyName}} build trusted customer proof — like reviews, testimonials, use cases, and reference calls.',
      'What you can do once you join:\n- Write reviews about your experience with {{productNames}}\n- Share video or written testimonials and use cases\n- Take reference calls with prospects considering {{companyName}}',
    ],
    defaultCtaLabel: 'Join the workspace',
    defaultClosingText: 'If you have any issue accessing the workspace, just reply to this email and we\'ll help.',
    variables: PROMOTER_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: 'magicLink',
  },
  [EmailTemplateType.PROSPECT_MAGIC_LINK]: {
    label: 'Invite Prospect',
    description: 'Sent when you invite a new prospect',
    defaultSubject: "You've been matched with a customer reference for {{companyName}}",
    defaultGreeting: 'Hi {{firstName}},',
    defaultBodyParagraphs: [
      '{{vendorName}} from {{companyName}} invited you to connect with one of their customer promoters through Clareefai.',
      'Clareefai helps teams make customer reference calls easy to request, book and manage.',
      'What you can do once you join:\n- Access the customer reference you\'ve been matched with\n- Review the promoter\'s profile and context\n- Connect directly through Clareefai',
    ],
    defaultCtaLabel: 'Access Your Connection',
    defaultClosingText: 'If you have any issue accessing it, just reply to this email and we\'ll help.',
    variables: PROSPECT_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: 'magicLink',
  },
  [EmailTemplateType.PASSWORDLESS_LOGIN]: {
    label: 'Passwordless Login',
    description: 'Sent when a user requests a passwordless login link',
    defaultSubject: "You're invited – sign in to your account",
    defaultGreeting: "You're invited, {{firstName}}!",
    defaultBodyParagraphs: [
      'Invited by: {{companyName}} ({{vendorName}})\nWhy: {{invitationReason}}',
      'Click the button below to sign in to your account:',
    ],
    defaultCtaLabel: 'Sign In to Your Account',
    defaultClosingText: 'This link expires in 24 hours and can only be used once. If you didn\'t request this, please ignore this email.',
    variables: PASSWORDLESS_LOGIN_VARIABLES,
    maxParagraphs: 3,
    ctaLinkVariable: 'loginLink',
  },
  [EmailTemplateType.TEAM_MEMBER_INVITE]: {
    label: 'Invite Team Member',
    description: 'Sent when you invite a team member to join your workspace',
    defaultSubject: "You've been invited to Clareefai",
    defaultGreeting: "You've been invited to Clareefai",
    defaultBodyParagraphs: [
      'Welcome, {{firstName}},\n{{inviterName}} from {{companyName}} invited you to join their team on Clareefai as {{role}}.',
      'With this access, you\'ll be able to collaborate with the team and manage key responsibilities.',
      'Set your password to get started:',
    ],
    defaultCtaLabel: 'Set Your Password',
    defaultClosingText: 'This link expires in 24 hours. If it expires, ask {{inviterName}} to resend an invite.',
    variables: TEAM_MEMBER_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: 'magicLink',
  },
  [EmailTemplateType.REWARD_GIFT_CARD]: {
    label: 'Reward — Gift Card',
    description: 'Sent when a promoter earns a gift card reward (Tremendous)',
    defaultSubject: "You've earned a reward!",
    defaultGreeting: 'Congratulations, {{firstName}}!',
    defaultBodyParagraphs: [
      "Great news! You've earned a reward from {{vendorName}}.",
      'Your gift card will be processed and delivered to you shortly via Tremendous. You\'ll receive a separate email with instructions on how to claim it.',
      'Thank you for your contribution and continued support!',
    ],
    defaultCtaLabel: '',
    defaultClosingText: 'Questions? Contact {{vendorName}} for assistance.',
    variables: REWARD_GIFT_CARD_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: '',
  },
  [EmailTemplateType.REWARD_DISCOUNT]: {
    label: 'Reward — Discount',
    description: 'Sent when a promoter earns a discount reward',
    defaultSubject: "You've earned a discount!",
    defaultGreeting: 'Congratulations, {{firstName}}!',
    defaultBodyParagraphs: [
      "Great news! You've earned a discount from {{vendorName}}: {{discountName}}.",
      'To apply your discount, please get in touch with {{vendorName}}. {{discountTerms}}',
      'Thank you for your contribution and continued support!',
    ],
    defaultCtaLabel: '',
    defaultClosingText: 'Questions? Contact {{vendorName}} for assistance.',
    variables: REWARD_DISCOUNT_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: '',
  },
  [EmailTemplateType.REWARD_CHOICE]: {
    label: 'Reward — Choice',
    description: 'Sent when a promoter can choose between a gift card and a discount',
    defaultSubject: 'Choose your reward!',
    defaultGreeting: 'Congratulations, {{firstName}}!',
    defaultBodyParagraphs: [
      "Great news! You've earned a reward from {{vendorName}} — and you get to choose how to receive it.",
      'You can pick a {{currency}} {{rewardAmount}} gift card or {{discountName}}. Open your Rewards page in Clareefai to choose.',
      'Thank you for your contribution and continued support!',
    ],
    defaultCtaLabel: '',
    defaultClosingText: 'Questions? Contact {{vendorName}} for assistance.',
    variables: REWARD_CHOICE_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: '',
  },
  [EmailTemplateType.SURVEY_INVITATION]: {
    label: 'Survey Invitation',
    description: 'Sent when you invite someone to complete an NPS survey',
    defaultSubject: 'We value your feedback, {{firstName}}!',
    defaultGreeting: 'Hi {{firstName}},',
    defaultBodyParagraphs: [
      '{{vendorName}} from {{companyName}} would love to hear your thoughts. Your feedback helps us improve and deliver an even better experience.',
      "What you'll help with:\n- Rate your experience working with {{companyName}}\n- Share feedback on what we do well and where we can improve\n- Help shape the future of our product and services",
      'This survey takes about 2 minutes. Your feedback is confidential.',
    ],
    defaultCtaLabel: 'Take the Survey',
    defaultClosingText: 'Your feedback is confidential and will only be used to improve our services.',
    variables: SURVEY_VARIABLES,
    maxParagraphs: 4,
    ctaLinkVariable: 'surveyLink',
  },
};
